import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization} from '../../../../lib/api';
import {FetchProvidersResponse, FetchGeoJsonResponse} from '../types';

/**
 * Manages provider data including local and existing providers, provider GeoJSON for maps,
 * and provider selection state (additions/deletions). Handles provider list loading and column visibility.
 */
export class ProviderStore {
    private _geoJson: FetchGeoJsonResponse | null = null;
    private _localProviders: FetchProvidersResponse = [];
    private _existingProviders: FetchProvidersResponse = [];
    selectedAdditionalKeys: string[] = [];
    deselectedExistingKeys: string[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    get localProviders(): FetchProvidersResponse {
        return this._localProviders;
    }

    set localProviders(value: FetchProvidersResponse) {
        this._localProviders = value;
    }

    get existingProviders(): FetchProvidersResponse {
        return this._existingProviders;
    }

    set existingProviders(value: FetchProvidersResponse) {
        this._existingProviders = value;
    }

    get geoJson(): FetchGeoJsonResponse | null {
        return this._geoJson;
    }

    set geoJson(value: FetchGeoJsonResponse | null) {
        this._geoJson = value;
    }

    selectAdditionalProviders(keys: string[]): void {
        const existingProviders = this._existingProviders.filter(p => keys.includes(p['id']));
        this.deselectedExistingKeys = this.deselectedExistingKeys.filter(n => !existingProviders.some(p => p['id'] === n));
        const newProviders = keys.filter(k => !this._existingProviders.some(p => p['id'] === k));
        const combined = [...this.selectedAdditionalKeys, ...newProviders];
        this.selectedAdditionalKeys = [...new Set(combined)];
    }

    deselectAdditionalProviders(keys: string[]): void {
        this.selectedAdditionalKeys = this.selectedAdditionalKeys.filter(
            (key) => !keys.includes(key)
        );
    }

    deselectAllAdditionalProviders(): void {
        this.selectedAdditionalKeys = [];
    }

    selectExistingProviders(keys: string[]): void {
        this.deselectedExistingKeys = this.deselectedExistingKeys.filter(
            (key) => !keys.includes(key)
        );
    }

    selectAllExistingProviders(): void {
        this.deselectedExistingKeys = [];
    }

    deselectExistingProviders(keys: string[]): void {
        keys.forEach((key) => {
            if (!this.deselectedExistingKeys.includes(key)) {
                this.deselectedExistingKeys.push(key);
            }
        });
    }

    deselectAllExistingProviders(): void {
        this.deselectedExistingKeys = this._existingProviders.map(p => p['id']);
    }

    appendLocalProviders(providers: FetchProvidersResponse): void {
        this._localProviders.push(...providers);
    }

    async loadLocalProviders(
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        distanceCalcType: string,
        controller: AbortController
    ): Promise<{current_page: number, total_pages: number, total_count: number} | null> {
        const [localProvidersResponse, existingProviders] = await Promise.all([
            networkOptimization.fetchProviders(filters, runId, revisionId, distanceCalcType, controller.signal),
            networkOptimization.fetchProviderReport(filters, runId, revisionId, controller.signal)
        ]) as [any, FetchProvidersResponse];

        // Handle paginated response format
        let localProviders: FetchProvidersResponse;
        let paginationInfo: {current_page: number, total_pages: number, total_count: number} | null = null;

        if (Array.isArray(localProvidersResponse)) {
            // Backward compatible: array response when count <= 5000
            localProviders = localProvidersResponse;
        } else {
            // Paginated response when count > 5000
            localProviders = localProvidersResponse.data || [];
            paginationInfo = {
                current_page: localProvidersResponse.current_page,
                total_pages: localProvidersResponse.total_pages,
                total_count: localProvidersResponse.total_count
            };
        }

        runInAction(() => {
            this._localProviders = localProviders.map(provider => ({...provider, id: provider.provider_address_fact_key}));
            this._existingProviders = existingProviders.map(provider => ({...provider, id: provider.provider_address_fact_key}));
        });

        return paginationInfo;
    }

    async loadDisruptionAnalysisProviders(
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        controller: AbortController
    ): Promise<void> {
        const [localProviders, existingProviders] = await Promise.all([
            networkOptimization.fetchDAProviders(filters, runId, revisionId, controller.signal),
            networkOptimization.fetchDAProviderReport(filters, runId, revisionId, controller.signal)
        ]) as [FetchProvidersResponse, FetchProvidersResponse];

        runInAction(() => {
            this._localProviders = localProviders.map(provider => ({...provider, id: provider.provider_address_key}));
            this._existingProviders = existingProviders.map(provider => ({...provider, id: provider.provider_address_key}));
        });
    }

    async loadProviderGeoJson(
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        controller: AbortController
    ): Promise<void> {
        const response = await networkOptimization.fetchProviderGeoJson(
            filters,
            runId,
            revisionId,
            controller.signal
        );
        runInAction(() => {
            this._geoJson = response;
        });
    }

    async loadDisruptionAnalysisProviderGeoJson(
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        controller: AbortController
    ): Promise<void> {
        const response = await networkOptimization.fetchDAProviderGeoJson(
            filters,
            runId,
            revisionId,
            controller.signal
        );
        runInAction(() => {
            this._geoJson = response;
        });
    }

    /**
     * Reset provider store state to initial values
     */
    reset(): void {
        runInAction(() => {
            this._localProviders = [];
            this._existingProviders = [];
            this.selectedAdditionalKeys = [];
            this.deselectedExistingKeys = [];
        });
    }
}
